import { SquareButton, DropdownContext, IndexedObject } from "@themuse/design-system";
// eslint-disable-next-line import/no-extraneous-dependencies
import { useCombobox, UseComboboxStateChange } from "downshift";
import React from "react";
import { useSearchDispatch, useSearchState } from "hooks/SearchContext";
import {
    CompanyActionTypes, FiltersActionTypes, KeywordActionTypes, Location,
    LocationActionTypes, UserInteractionActionTypes
} from "interfaces/context/searchContextInterfaces";
import { formatLocations } from "orm/locations";
import { locationSuggestionsSelector } from "selectors/index";
import { fetchCompanySuggestions, fetchLocationSuggestions } from "utils/api";
import { isMobile } from "utils/browser";
import { initialRadiusValue } from "utils/searchFacetItems";
import CompanySearchInput from "./CompanySearchInput";
import KeywordSearchInput from "./KeywordSearchInput";
import LocationSearchInput from "./LocationSearchInput";
import styles from "./SearchInput.module.scss";

interface SearchInputProps {
    clearInputs?: boolean;
    onClear?: () => void;
}

interface SearchButtonProps {
    onClick?: () => void;
}

const SearchButton = (props: SearchButtonProps) => {
    const { onClick } = props;

    return (
        <SquareButton className={styles.inputSubmit} color="secondary-100" onClick={onClick}>
            Search
        </SquareButton>
    );
};

const NoResults = () => {
    const { onClearButtonClick } = React.useContext(DropdownContext);
    return (
        <>
            No matches. Please&nbsp;
            <button className={styles.noResultsButton} onClick={onClearButtonClick} type="button">
                try another search.
            </button>
        </>
    );
};

const NoResultsKeyword = () => {
    const { closeMenu, inputValue } = React.useContext(DropdownContext);

    return (
        <button className={styles.noResultsButtonKeyword} onClick={closeMenu} type="button">
            See all results for
            &quot;
            {inputValue}
            &quot;
        </button>
    );
};

const SearchInput = (props: SearchInputProps) => {
    const { clearInputs, onClear } = props;
    const searchState = useSearchState();
    const searchDispatch = useSearchDispatch();

    const clearValues = () => {
        onClear?.();
    };

    const setLocationSuggestions = async (locationInput: string) => {
        if (typeof locationInput === "undefined") {
            return;
        }

        const rawSuggestions = await fetchLocationSuggestions(locationInput);
        const suggestions = formatLocations(rawSuggestions);
        searchDispatch({
            type: LocationActionTypes.updateLocationSuggestions,
            payload: {
                locationSuggestions: suggestions
            }
        });
    };

    const setCompanySuggestions = async (companyInput: string) => {
        if (typeof companyInput === "undefined") return;
        const suggestions = await fetchCompanySuggestions(companyInput);
        searchDispatch({
            type: CompanyActionTypes.updateCompanySuggestions,
            payload: {
                companySuggestions: suggestions
            }
        });
    };

    React.useEffect(() => {
        setLocationSuggestions(searchState.location?.userInputValue);
    }, [searchState.location?.userInputValue]);

    React.useEffect(() => {
        setCompanySuggestions(searchState.company?.userInputValue);
    }, [searchState.company?.userInputValue]);

    const updateLocationInput = (changes: UseComboboxStateChange<IndexedObject>) => {
        const { inputValue, selectedItem, type } = changes;
        // item was clicked from dropdown
        const itemClicked = type === useCombobox.stateChangeTypes.ItemClick;
        // reset button was clicked with menu open
        const resetClickedMenuOpen = type === useCombobox.stateChangeTypes.FunctionReset;
        // reset button was clicked with menu closed
        const resetClickedMenuClosed = type === useCombobox.stateChangeTypes.FunctionOpenMenu;
        // input was cleared from input field
        const inputValueCleared = type === useCombobox.stateChangeTypes.InputChange && inputValue === "";
        // user has entered a value into the input field
        const isEnteringData = type === useCombobox.stateChangeTypes.InputChange && inputValue !== "";

        if (isEnteringData || itemClicked) {
            searchDispatch({
                type: LocationActionTypes.updateLocation,
                payload: {
                    userInputValue: inputValue,
                    userSelectedLocation: selectedItem as Location
                }
            });
        }

        if (itemClicked) {
            // set initial radius if not remote/flexible
            if (!selectedItem?.remote_work_location_type) {
                searchDispatch({
                    type: FiltersActionTypes.updateFilter,
                    payload: {
                        radius: initialRadiusValue
                    }
                });
            }
        }

        if (resetClickedMenuOpen || resetClickedMenuClosed || inputValueCleared) {
            searchDispatch({
                type: LocationActionTypes.updateLocation,
                payload: {
                    userInputValue: inputValue,
                    userSelectedLocation: null
                }
            });

            if (searchState?.filters?.radius) {
                searchDispatch({
                    type: FiltersActionTypes.updateFilter,
                    payload: {
                        radius: ""
                    }
                });
            }
        }
    };

    const updateCompanyInput = (changes: UseComboboxStateChange<IndexedObject>) => {
        const { inputValue, selectedItem } = changes;

        searchDispatch({
            type: CompanyActionTypes.updateCompany,
            payload: {
                userInputValue: inputValue,
                userSelectedCompany: selectedItem
            }
        });

        if (selectedItem) {
            searchDispatch({
                type: FiltersActionTypes.updateFilter,
                payload: {
                    industry: [],
                    perks: [],
                    companySize: []
                }
            });
        }
    };

    const updateKeywordInput = (changes: UseComboboxStateChange<IndexedObject>) => {
        const { inputValue, selectedItem } = changes;

        // New keyword implementation
        searchDispatch({
            type: KeywordActionTypes.updateKeyword,
            payload: {
                userInputValue: inputValue,
                userSelectedKeyword: selectedItem
            }
        });
    };

    const onSearchButtonClick = () => {
        if (!isMobile()) {
            searchDispatch({
                type: UserInteractionActionTypes.updateFilterInteraction,
                payload: {
                    searchButtonClickedAt: Date.now()
                }
            });
        }
    };

    const formStyles = styles.form;

    return (
        <form className={formStyles}>
            <div style={{ position: "relative" }}>
                <KeywordSearchInput
                    keyword={searchState.keyword}
                    clear={clearInputs}
                    onClear={clearValues}
                    onInputChange={updateKeywordInput}
                    noResultsMessage={<NoResultsKeyword />}
                />
            </div>
            <CompanySearchInput
                company={searchState.company}
                clear={clearInputs}
                onClear={clearValues}
                onInputChange={updateCompanyInput}
                noResultsMessage={<NoResults />}
            />
            <LocationSearchInput
                clear={clearInputs}
                onClear={clearValues}
                onInputChange={updateLocationInput}
                location={{
                    ...searchState.location,
                    locationSuggestions: locationSuggestionsSelector(searchState.location)
                }}
                noResultsMessage={<NoResults />}
            />
            <SearchButton onClick={onSearchButtonClick} />
        </form>
    );
};

SearchInput.displayName = "SearchInput";
export default SearchInput;
